import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/adminService';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    students: 0,
    admissions: 0,
    employees: { total: 0, teachers: 0, staff: 0, designations: {} },
    classes: 0,
    coupons: 0,
    feeStructures: 0,
    lectures: 0,
    attendance: { present: 0, absent: 0, late: 0 },
    exams: { total: 0, upcoming: 0 }
  });
    

  const [classData, setClassData] = useState([]);
  const [recentAdmissions, setRecentAdmissions] = useState([]);
  const [upcomingExams, setUpcomingExams] = useState([]);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});

  useEffect(() => {
    // Simulate API delay
    setTimeout(() => {
        fetchDashboardData();
    }, 1000);
  }, []);

  const fetchDashboardData = async () => {
    try { 

      setLoading(true);
        const response = await adminAPI.getAdminDashboard();
              const data = response.data.data || [];
              setStats(data);

      setStats({
        students: data.totalStudents || '',
        admissions: data.totalAdmissions || '',
        employees: data.totalEmployees || '',
        classes: data.totalClasses || '',
        coupons: data.totalCoupons || '',
        feeStructures: data.totalFeeStructures || '',
        lectures: data.totalLectures || '',
        attendance: data.totalAttendance || '',
        exams: data.totalExams || ''
      });

      // Prepare Chart Data
      const documentStyle = getComputedStyle(document.documentElement);
      const textColor = documentStyle.getPropertyValue('--text-color');
      const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
      const surfaceBorder = documentStyle.getPropertyValue('--surface-border');

      const barData = {
        labels: data.classStudentCount.map(c => c.name),
        datasets: [
            {
                label: 'Students per Class',
                backgroundColor: '#6366f1',
                borderColor: '#6366f1',
                data: data.classStudentCount.map(c => c.studentCount)
            }
        ]
      };

      const pieData = {
        labels: ['Teachers', 'Staff'],
        datasets: [
            {
                data: [data.totalEmployees.teachers, data.totalEmployees.staff],
                backgroundColor: ['#3b82f6', '#10b981'],
                hoverBackgroundColor: ['#60a5fa', '#34d399']
            }
        ]
      };

      const attendanceChartData = {
        labels: ['Present', 'Absent', 'Late'],
        datasets: [
            {
                data: [data.totalAttendance.present, data.totalAttendance.absent, data.totalAttendance.late],
                backgroundColor: ['#10B981', '#EF4444', '#F59E0B'],
                hoverBackgroundColor: ['#34D399', '#F87171', '#FBBF24']
            }
        ]
      };

      setChartData({ bar: barData, pie: pieData, attendance: attendanceChartData });
      
      setChartOptions({
        bar: {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: { legend: { labels: { fontColor: textColor } } },
            scales: {
                x: { ticks: { color: textColorSecondary, font: { weight: 500 } }, grid: { display: false, drawBorder: false } },
                y: { ticks: { color: textColorSecondary }, grid: { color: surfaceBorder, drawBorder: false } }
            }
        },
        pie: {
            plugins: { legend: { labels: { usePointStyle: true } } }
        },
        attendance: {
            cutout: '60%',
            plugins: { legend: { position: 'bottom' } }
        }
      });

    } catch (error) {
      console.error("Error fetching dashboard data", error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtext, onClick }) => (
    <div 
      onClick={onClick}
      className={`p-4 rounded-xl shadow-lg text-white ${color} transform transition-all hover:scale-105 cursor-pointer relative overflow-hidden`}
    >
      <div className="absolute right-0 top-0 h-full w-16 bg-white opacity-10 transform skew-x-12 translate-x-8"></div>
      <div className="flex justify-between items-center mb-2 relative z-10">
        <h3 className="text-lg font-semibold opacity-90">{title}</h3>
        <i className={`pi ${icon} text-2xl opacity-80`}></i>
      </div>
      <div className="text-3xl font-bold relative z-10">{value}</div>
      {subtext && <div className="text-sm opacity-75 mt-1 relative z-10">{subtext}</div>}
    </div>
  );

  const QuickAction = ({ label, icon, color, onClick }) => (
    <button 
      onClick={onClick}
      className={`flex flex-col items-center justify-center p-4 rounded-xl bg-white shadow-md hover:shadow-lg transition-all border border-gray-100 group`}
    >
      <div className={`p-3 rounded-full ${color} bg-opacity-10 text-opacity-100 mb-2 group-hover:scale-110 transition-transform`}>
        <i className={`pi ${icon} text-xl`}></i>
      </div>
      <span className="text-sm font-medium text-gray-600 group-hover:text-gray-900">{label}</span>
    </button>
  );

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><i className="pi pi-spin pi-spinner text-4xl text-blue-500"></i></div>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard Overview</h1>
          <p className="text-gray-500">Welcome back, {user?.username || 'User'}</p>
        </div>
        <div className="text-right hidden md:block">
          <p className="text-sm text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <QuickAction label="Add Student" icon="pi-user-plus" color="bg-blue-500 text-blue-600" onClick={() => navigate('/students')} />
          <QuickAction label="Take Attendance" icon="pi-check-circle" color="bg-green-500 text-green-600" onClick={() => navigate('/attendance')} />
          <QuickAction label="Create Exam" icon="pi-file-edit" color="bg-purple-500 text-purple-600" onClick={() => navigate('/exams')} />
          <QuickAction label="Add Employee" icon="pi-briefcase" color="bg-orange-500 text-orange-600" onClick={() => navigate('/employees')} />
          <QuickAction label="Collect Fees" icon="pi-wallet" color="bg-pink-500 text-pink-600" onClick={() => navigate('/fees')} />
          <QuickAction label="Generate Coupon" icon="pi-ticket" color="bg-teal-500 text-teal-600" onClick={() => navigate('/coupons')} />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard 
          title="Total Students" 
          value={stats.students} 
          icon="pi-users" 
          color="bg-gradient-to-r from-blue-500 to-blue-600" 
          onClick={() => navigate('/students')}
        />
        <StatCard 
          title="Attendance Today" 
          value={`${stats.attendance.present}`} 
          subtext={`${stats.attendance.absent} Absent`}
          icon="pi-calendar-check" 
          color="bg-gradient-to-r from-green-500 to-green-600" 
          onClick={() => navigate('/attendance')}
        />
        <StatCard 
          title="Employees" 
          value={stats.employees.total} 
          icon="pi-briefcase" 
          color="bg-gradient-to-r from-purple-500 to-purple-600" 
          subtext={`${stats.employees.teachers} Teachers`}
          onClick={() => navigate('/employees')}
        />
        <StatCard 
          title="Upcoming Exams" 
          value={stats.exams.upcoming} 
          icon="pi-file" 
          color="bg-gradient-to-r from-indigo-500 to-indigo-600" 
          subtext="Next 7 days"
          onClick={() => navigate('/exams')}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <Card title="Attendance Overview" className="shadow-md rounded-xl lg:col-span-1">
            <div className="flex justify-center h-64">
                <Chart type="doughnut" data={chartData.attendance} options={chartOptions.attendance} className="w-full" />
            </div>
            <div className="mt-4 text-center">
                <span className="text-2xl font-bold text-gray-800">
                    {stats.attendance.present + stats.attendance.absent > 0 
                        ? Math.round((stats.attendance.present / (stats.attendance.present + stats.attendance.absent)) * 100) 
                        : 0}%
                </span>
                <p className="text-sm text-gray-500">Present Today</p>
            </div>
        </Card>
        
        <Card title="Students per Class" className="shadow-md rounded-xl lg:col-span-2">
            <Chart type="bar" data={chartData.bar} options={chartOptions.bar} height="300px" />
        </Card>
      </div>

      {/* Detailed Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card title="Recent Admissions" className="shadow-md rounded-xl">
            <DataTable value={recentAdmissions} paginator rows={5} className="p-datatable-sm" emptyMessage="No recent admissions">
                <Column field="studentName" header="Name" sortable></Column>
                <Column field="class" header="Class" sortable></Column>
                <Column field="status" header="Status" body={(rowData) => (
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        rowData.status === 'approved' ? 'bg-green-100 text-green-800' : 
                        rowData.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                        {rowData.status || 'Pending'}
                    </span>
                )}></Column>
            </DataTable>
        </Card>

        <Card title="Upcoming Exams" className="shadow-md rounded-xl">
             <DataTable value={upcomingExams} className="p-datatable-sm" emptyMessage="No upcoming exams">
                <Column field="subject" header="Subject"></Column>
                <Column field="class" header="Class"></Column>
                <Column field="date" header="Date" body={(rowData) => new Date(rowData.date).toLocaleDateString()}></Column>
            </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
